import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'
import { isDateExpiring } from '@/lib/utils/dates'
import { transformReserveOrder } from '@/lib/utils/transformEntries'

import { useCartStore } from '@/stores/cart'
import { ApiReserveAllocationItem, ApiReserveOrdersResponse, ReserveOrder } from '@/types'

type State = {
  isReserveOrdersExist: boolean
  isExpiringReserveOrdersExistLoaded: boolean
  isExpiringReserveOrdersExist: boolean
}
type Actions = {
  getReserveOrders: (params: {
    query: string
    sort: string
    currentPage: number
    pageSize: number
    showCompanyOrders: boolean
  }) => Promise<{
    reserveOrders: ReserveOrder[]
    pagination: ApiReserveOrdersResponse['pagination']
  }>
  setIsReserveOrdersExist: () => Promise<void>
  setIsExpiringReserveOrdersExist: () => Promise<void>
  getAllocationDetails: (params: {
    reserveId: string
    entryNumber: string
    lotId: string
  }) => Promise<ApiReserveAllocationItem>
  removeReserve: (payload: {
    reserveId: string
    entryNumber: string
    lotsToRemove: {
      lot: Array<{
        lot: string
        quantity: number
      }>
    }
  }) => Promise<void>
  resetState: () => void
}

export const useReserveOrdersStore = defineStore<string, State, Record<string, never>, Actions>(
  'reserve-orders',
  {
    state: (): State => ({
      isReserveOrdersExist: false,
      isExpiringReserveOrdersExistLoaded: false,
      isExpiringReserveOrdersExist: false,
    }),

    actions: {
      async getReserveOrders(params) {
        const response = await api.reserveOrders.getReserveOrders(params)
        const cartStore = useCartStore()
        const transformedReserves = response.data.entries.map((el) =>
          transformReserveOrder(el, cartStore.inventoryProducts),
        )
        return {
          reserveOrders: transformedReserves,
          pagination: response.data.pagination,
        }
      },

      async setIsReserveOrdersExist() {
        const params = { pageSize: 1, showCompanyOrders: true }
        const response = await api.reserveOrders.getReserveOrders(params)
        this.isReserveOrdersExist = !!response.data.entries.length
      },

      async setIsExpiringReserveOrdersExist() {
        const params = { sort: 'byEndDateASC', pageSize: 1, showCompanyOrders: true }
        const response = await api.reserveOrders.getReserveOrders(params)
        const reserves = response.data.entries
        const isSomeExpiring = reserves.some((reserve) => isDateExpiring(reserve.endDate, 30))
        this.isExpiringReserveOrdersExist = isSomeExpiring
        this.isExpiringReserveOrdersExistLoaded = true
      },

      async getAllocationDetails({ reserveId, entryNumber, lotId }) {
        const response = await api.reserveOrders.getAllocationDetails({
          reserveId,
          entryNumber,
          lotId,
        })
        return response.data
      },

      async removeReserve(payload) {
        await api.reserveOrders.removeReserve(payload)
      },

      resetState() {
        this.$reset()
      },
    },
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useReserveOrdersStore, import.meta.hot))
}
